import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Trophy, Clock, Users, TrendingUp, ExternalLink, Crown, Award, Star, Zap, Gift, RefreshCw } from 'lucide-react';
import { campaignData, prizes, ticketHolders as mockTicketHolders, motivationalQuotes, ctaLink } from '../data/mock';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO_URL = "https://customer-assets.emergentagent.com/job_megadraw-trader/artifacts/mmbzolkl_logo%20ftc.png";

// Floating particles component
const FloatingParticles = () => {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {[...Array(15)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute w-1 h-1 bg-[#ff0080]/30 rounded-full"
          style={{
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
          }}
          animate={{
            y: [0, -150],
            opacity: [0, 1, 0],
          }}
          transition={{
            duration: Math.random() * 3 + 2,
            repeat: Infinity,
            repeatDelay: Math.random() * 2,
          }}
        />
      ))}
    </div>
  );
};

// Prize card component with rank-specific styling
const PrizeCard = ({ prize, index }) => {
  const isFirst = index === 0;
  const isSecond = index === 1;
  const isThird = index === 2;
  
  const getCardStyles = () => {
    if (isFirst) {
      return {
        bg: 'bg-gradient-to-b from-[#ffd700]/20 via-[#ffd700]/10 to-transparent',
        border: 'border-[#ffd700]/50 hover:border-[#ffd700]',
        glow: 'shadow-[0_0_40px_rgba(255,215,0,0.3)]',
        textColor: 'text-[#ffd700]',
        scale: 'lg:scale-110',
      };
    }
    if (isSecond) {
      return {
        bg: 'bg-gradient-to-b from-[#c0c0c0]/15 via-[#c0c0c0]/5 to-transparent',
        border: 'border-[#c0c0c0]/40 hover:border-[#c0c0c0]',
        glow: 'shadow-[0_0_30px_rgba(192,192,192,0.2)]',
        textColor: 'text-[#e0e0e0]',
        scale: '',
      };
    }
    if (isThird) {
      return {
        bg: 'bg-gradient-to-b from-[#cd7f32]/15 via-[#cd7f32]/5 to-transparent',
        border: 'border-[#cd7f32]/40 hover:border-[#cd7f32]',
        glow: 'shadow-[0_0_25px_rgba(205,127,50,0.2)]',
        textColor: 'text-[#cd7f32]',
        scale: '',
      };
    }
    return {
      bg: 'bg-gradient-to-b from-white/5 to-transparent',
      border: 'border-white/10 hover:border-[#ff0080]/50',
      glow: '',
      textColor: 'text-[#ff0080]',
      scale: '',
    };
  };

  const styles = getCardStyles();

  const getRankIcon = () => {
    if (isFirst) {
      return (
        <div className="relative">
          <motion.div
            className="absolute -top-8 left-1/2 -translate-x-1/2"
            animate={{ y: [0, -5, 0], rotate: [0, 5, -5, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <Crown className="w-10 h-10 text-[#ffd700] drop-shadow-[0_0_10px_rgba(255,215,0,0.5)]" />
          </motion.div>
          <div className="w-16 h-16 mx-auto rounded-full bg-gradient-to-br from-[#ffd700] via-[#ffed4a] to-[#ffd700] flex items-center justify-center shadow-[0_0_30px_rgba(255,215,0,0.5)]">
            <span className="text-2xl font-black text-black">1</span>
          </div>
        </div>
      );
    }
    if (isSecond) {
      return (
        <div className="w-14 h-14 mx-auto rounded-full bg-gradient-to-br from-[#c0c0c0] via-[#e8e8e8] to-[#c0c0c0] flex items-center justify-center shadow-[0_0_20px_rgba(192,192,192,0.4)]">
          <span className="text-xl font-black text-black">2</span>
        </div>
      );
    }
    if (isThird) {
      return (
        <div className="w-12 h-12 mx-auto rounded-full bg-gradient-to-br from-[#cd7f32] via-[#dfa66d] to-[#cd7f32] flex items-center justify-center shadow-[0_0_15px_rgba(205,127,50,0.4)]">
          <span className="text-lg font-black text-black">3</span>
        </div>
      );
    }
    return (
      <div className="w-10 h-10 mx-auto rounded-full bg-gradient-to-br from-[#ff0080]/50 to-[#c026d3]/50 flex items-center justify-center border border-[#ff0080]/30">
        <Gift className="w-5 h-5 text-white/80" />
      </div>
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 30, scale: 0.9 }}
      whileInView={{ opacity: 1, y: 0, scale: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      whileHover={{ y: -10, scale: isFirst ? 1.15 : 1.05 }}
      className={`relative ${styles.bg} backdrop-blur-md border ${styles.border} rounded-3xl p-6 text-center transition-all duration-500 ${styles.glow} ${styles.scale}`}
    >
      {isFirst && (
        <motion.div
          className="absolute inset-0 rounded-3xl bg-gradient-to-r from-[#ffd700]/0 via-[#ffd700]/10 to-[#ffd700]/0"
          animate={{ x: [-200, 200] }}
          transition={{ duration: 3, repeat: Infinity, repeatType: 'reverse' }}
        />
      )}
      
      <div className="relative z-10">
        <div className={`mb-4 ${isFirst ? 'pt-4' : ''}`}>
          {getRankIcon()}
        </div>
        
        <span className={`text-xs font-mono uppercase tracking-widest ${isFirst ? 'text-[#ffd700]' : 'text-white/50'}`}>
          {prize.position}
        </span>
        
        <motion.div 
          className={`text-4xl md:text-5xl font-black my-3 ${styles.textColor}`}
          animate={isFirst ? { scale: [1, 1.02, 1] } : {}}
          transition={{ duration: 2, repeat: Infinity }}
        >
          ${prize.amount}
        </motion.div>
        
        <span className="text-white/70 text-sm font-medium">{prize.label}</span>
        {prize.perWinner && (
          <span className="block text-white/40 text-xs mt-1">(each winner)</span>
        )}
      </div>
    </motion.div>
  );
};

const LandingPage = () => {
  const [ticketHolders, setTicketHolders] = useState([]);
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [totalEntries, setTotalEntries] = useState(0);

  // Fetch leaderboard data from backend
  useEffect(() => {
    const fetchLeaderboard = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API}/leaderboard`);
        if (response.data && response.data.data) {
          // Transform API response to match component format
          const transformedData = response.data.data.map(item => ({
            name: item.name,
            tickets: item.tickets
          }));
          setTicketHolders(transformedData);
          setLastUpdated(response.data.last_updated);
          setTotalEntries(response.data.total_entries);
        }
      } catch (error) {
        console.error('Error fetching leaderboard:', error);
        // Fallback to mock data if API fails
        setTicketHolders(mockTicketHolders);
      } finally {
        setLoading(false);
      }
    };

    fetchLeaderboard();
  }, []);

  // Calculate countdown
  useEffect(() => {
    const targetDate = new Date('2025-12-28T23:59:59');
    
    const calculateTimeLeft = () => {
      const now = new Date();
      const difference = targetDate - now;
      
      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / 1000 / 60) % 60),
          seconds: Math.floor((difference / 1000) % 60)
        });
      }
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);
    return () => clearInterval(timer);
  }, []);

  const maxTickets = ticketHolders.length > 0 ? ticketHolders[0].tickets : 1;

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.08, delayChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { type: "spring", stiffness: 100, damping: 12 }
    }
  };

  return (
    <div className="min-h-screen bg-[#050508] overflow-x-hidden">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Premium Background with distinct color */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-b from-[#2a0a3d] via-[#1a0828] to-[#0a0312]" />
          {/* Radial gradient overlay for depth */}
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(255,0,128,0.15)_0%,_transparent_60%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,_rgba(192,38,211,0.12)_0%,_transparent_50%)]" />
          {/* Subtle pattern */}
          <div className="absolute inset-0 opacity-20" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ff0080' fill-opacity='0.08'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>
        
        {/* Animated Glow Orbs */}
        <motion.div 
          className="absolute top-1/4 left-1/4 w-[300px] md:w-[500px] h-[300px] md:h-[500px] bg-[#ff0080]/20 rounded-full blur-[100px] md:blur-[150px]"
          animate={{ 
            scale: [1, 1.2, 1],
            opacity: [0.2, 0.3, 0.2],
          }}
          transition={{ duration: 4, repeat: Infinity }}
        />
        <motion.div 
          className="absolute bottom-1/4 right-1/4 w-[250px] md:w-[400px] h-[250px] md:h-[400px] bg-[#c026d3]/20 rounded-full blur-[80px] md:blur-[120px]"
          animate={{ 
            scale: [1.2, 1, 1.2],
            opacity: [0.25, 0.2, 0.25],
          }}
          transition={{ duration: 5, repeat: Infinity }}
        />
        <motion.div 
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[200px] md:w-[300px] h-[200px] md:h-[300px] bg-[#ffd700]/15 rounded-full blur-[80px] md:blur-[100px]"
          animate={{ 
            scale: [1, 1.3, 1],
            opacity: [0.15, 0.25, 0.15],
          }}
          transition={{ duration: 6, repeat: Infinity }}
        />

        <FloatingParticles />
        
        <motion.div 
          className="relative z-10 text-center px-6 max-w-5xl mx-auto"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: "easeOut" }}
        >
          {/* Logo */}
          <motion.div 
            className="mb-10"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <img 
              src={LOGO_URL} 
              alt="Fira Trading Challenge" 
              className="h-14 md:h-16 mx-auto filter brightness-0 invert drop-shadow-[0_0_20px_rgba(255,0,128,0.5)]"
            />
          </motion.div>
          
          {/* Main Headline with Premium Animation */}
          <motion.h1 
            className="text-5xl md:text-7xl lg:text-8xl font-black mb-6 leading-tight"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <span className="relative inline-block">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#ff0080] via-[#ff4da6] to-[#c026d3] drop-shadow-[0_0_30px_rgba(255,0,128,0.5)]">
                Mega Draw
              </span>
              <motion.span
                className="absolute -inset-1 bg-gradient-to-r from-[#ff0080]/20 via-transparent to-[#c026d3]/20 blur-xl"
                animate={{ opacity: [0.5, 1, 0.5] }}
                transition={{ duration: 2, repeat: Infinity }}
              />
            </span>
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#c026d3] via-[#ff4da6] to-[#ff0080]">
              Challenge
            </span>
          </motion.h1>
          
          <motion.p 
            className="text-xl md:text-2xl text-white/70 mb-4 font-light tracking-wide"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            Collect more tickets. Increase your chances. Win real rewards.
          </motion.p>

          {/* Campaign Timeline */}
          <motion.div 
            className="inline-flex items-center gap-3 md:gap-4 mt-2"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.8 }}
          >
            <div className="flex items-center gap-2 bg-white/5 backdrop-blur-sm border border-[#ff0080]/30 rounded-full px-4 py-2">
              <div className="w-2 h-2 bg-[#ff0080] rounded-full animate-pulse" />
              <span className="text-white/80 text-sm font-medium">10 Dec</span>
            </div>
            
            <div className="flex items-center gap-1">
              <motion.div 
                className="w-8 md:w-12 h-0.5 bg-gradient-to-r from-[#ff0080] to-[#c026d3]"
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ duration: 1, delay: 1 }}
              />
              <motion.div
                animate={{ x: [0, 5, 0] }}
                transition={{ duration: 1.5, repeat: Infinity }}
              >
                <Zap className="w-4 h-4 text-[#ffd700]" />
              </motion.div>
              <motion.div 
                className="w-8 md:w-12 h-0.5 bg-gradient-to-r from-[#c026d3] to-[#ff0080]"
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ duration: 1, delay: 1.2 }}
              />
            </div>
            
            <div className="flex items-center gap-2 bg-white/5 backdrop-blur-sm border border-[#ffd700]/30 rounded-full px-4 py-2">
              <Trophy className="w-4 h-4 text-[#ffd700]" />
              <span className="text-white/80 text-sm font-medium">28 Dec</span>
            </div>
          </motion.div>

        </motion.div>
        
        {/* Section Separator */}
        <div className="absolute bottom-0 left-0 right-0">
          <div className="h-px bg-gradient-to-r from-transparent via-[#ff0080]/50 to-transparent" />
        </div>
      </section>

      {/* Prize Pool Section */}
      <section className="py-16 px-6 relative">
        <div className="absolute inset-0 bg-[#0a0312]" />
        
        <div className="max-w-6xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <motion.div 
              className="inline-flex items-center gap-2 mb-4"
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
            >
              <Trophy className="w-6 h-6 text-[#ffd700]" />
              <span className="text-[#ffd700] font-mono uppercase tracking-widest text-sm">Prize Pool</span>
            </motion.div>
            
            <h2 className="text-4xl md:text-5xl font-black text-white mb-4">
              Win Your Share
            </h2>
            <p className="text-white/60 text-lg mb-2">Winners will be selected via ticket draw</p>
            <p className="text-white/50 text-xs md:text-sm mb-6 whitespace-nowrap">The lucky draw will be happening on 30th December 2025 at 9PM</p>
            
            {/* Total Prize Pool - Moved here */}
            <motion.div 
              className="inline-flex items-center gap-2 md:gap-4 bg-gradient-to-r from-[#ffd700]/10 via-[#ffd700]/20 to-[#ffd700]/10 border border-[#ffd700]/30 rounded-2xl px-4 md:px-8 py-3 md:py-4 shadow-[0_0_40px_rgba(255,215,0,0.15)]"
              whileHover={{ scale: 1.02 }}
              animate={{ boxShadow: ['0 0 40px rgba(255,215,0,0.15)', '0 0 60px rgba(255,215,0,0.25)', '0 0 40px rgba(255,215,0,0.15)'] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <Trophy className="w-6 h-6 md:w-8 md:h-8 text-[#ffd700] flex-shrink-0" />
              <span className="text-lg md:text-2xl lg:text-3xl font-black text-white whitespace-nowrap">
                Total Prize Pool: <span className="text-[#ffd700]">USD $2025</span>
              </span>
            </motion.div>
          </motion.div>

          {/* Prize Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-6 items-end">
            {prizes.map((prize, index) => (
              <PrizeCard key={index} prize={prize} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* Countdown Timer Section */}
      <section className="py-16 px-6 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0d0015] via-[#1a0525] to-[#0d0015]" />
        
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="inline-flex items-center gap-2 mb-6">
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
              >
                <Clock className="w-6 h-6 text-[#ff0080]" />
              </motion.div>
              <span className="text-[#ff0080] font-mono uppercase tracking-widest text-sm">Challenge Ends In</span>
            </div>
            
            <div className="grid grid-cols-4 gap-3 md:gap-6">
              {[
                { value: timeLeft.days, label: 'Days' },
                { value: timeLeft.hours, label: 'Hours' },
                { value: timeLeft.minutes, label: 'Minutes' },
                { value: timeLeft.seconds, label: 'Seconds' }
              ].map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ scale: 1.05, y: -5 }}
                  className="relative bg-gradient-to-b from-white/10 to-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-4 md:p-6 overflow-hidden group"
                >
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-t from-[#ff0080]/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"
                  />
                  <motion.div 
                    className="text-4xl md:text-6xl font-black text-white mb-2 relative z-10"
                    key={item.value}
                    initial={{ scale: 1.2, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ type: "spring", stiffness: 300 }}
                  >
                    {String(item.value).padStart(2, '0')}
                  </motion.div>
                  <div className="text-white/50 text-xs md:text-sm font-mono uppercase tracking-widest relative z-10">{item.label}</div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Top Ticket Holders Section */}
      <section className="py-16 px-6 relative">
        <div className="absolute inset-0 bg-[#050508]" />
        
        <div className="max-w-5xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <div className="inline-flex items-center gap-2 mb-4">
              <Users className="w-6 h-6 text-[#ff0080]" />
              <span className="text-[#ff0080] font-mono uppercase tracking-widest text-sm">Live Leaderboard</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-black text-white mb-3">Top 20 Ticket Holders</h2>
            <p className="text-white/60 text-lg">Higher tickets = Higher chances</p>
          </motion.div>

          {/* Participants count - outside table */}
          <motion.div 
            className="flex justify-end mb-3"
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <div className="inline-flex items-center gap-2 bg-[#ffd700]/10 border border-[#ffd700]/30 rounded-full px-4 py-2">
              <motion.div
                animate={{ opacity: [1, 0.4, 1], scale: [1, 1.1, 1] }}
                transition={{ duration: 1.5, repeat: Infinity }}
              >
                <Users className="w-5 h-5 text-[#ffd700]" />
              </motion.div>
              <span className="text-[#ffd700] font-bold text-sm md:text-base">577 Participants</span>
            </div>
          </motion.div>

          {loading ? (
            <div className="text-center text-white/50">Loading leaderboard...</div>
          ) : (
            <motion.div
              className="bg-gradient-to-b from-white/5 to-transparent backdrop-blur-md border border-white/10 rounded-3xl overflow-hidden shadow-[0_0_50px_rgba(255,0,128,0.1)]"
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              {/* Header - Desktop */}
              <div className="hidden md:grid grid-cols-12 gap-4 p-4 bg-gradient-to-r from-[#ff0080]/10 via-transparent to-[#c026d3]/10 border-b border-white/10">
                <div className="col-span-1 text-center text-white/50 text-sm font-mono uppercase">#</div>
                <div className="col-span-4 text-white/50 text-sm font-mono uppercase">Trader</div>
                <div className="col-span-2 text-center text-white/50 text-sm font-mono uppercase">Tickets</div>
                <div className="col-span-5 text-white/50 text-sm font-mono uppercase">Entry Strength</div>
              </div>

              {/* Header - Mobile */}
              <div className="md:hidden grid grid-cols-12 gap-2 p-3 bg-gradient-to-r from-[#ff0080]/10 via-transparent to-[#c026d3]/10 border-b border-white/10">
                <div className="col-span-2 text-center text-white/50 text-xs font-mono uppercase">#</div>
                <div className="col-span-6 text-white/50 text-xs font-mono uppercase">Trader</div>
                <div className="col-span-4 text-center text-white/50 text-xs font-mono uppercase">Tickets</div>
              </div>

              {/* Rows */}
              {ticketHolders.slice(0, 20).map((holder, index) => {
                const probability = (holder.tickets / maxTickets) * 100;
                const isHighChance = index < 5;
                
                return (
                  <motion.div
                    key={index}
                    variants={itemVariants}
                    whileHover={{ backgroundColor: 'rgba(255,255,255,0.05)' }}
                    className={`border-b border-white/5 transition-all ${
                      isHighChance ? 'bg-gradient-to-r from-[#ff0080]/5 via-transparent to-[#c026d3]/5' : ''
                    }`}
                  >
                    {/* Desktop Layout */}
                    <div className="hidden md:grid grid-cols-12 gap-4 p-4 items-center">
                      <div className="col-span-1 text-center">
                        <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold ${
                          isHighChance 
                            ? 'bg-gradient-to-br from-[#ff0080] to-[#c026d3] text-white shadow-[0_0_15px_rgba(255,0,128,0.4)]' 
                            : 'bg-white/10 text-white/70'
                        }`}>
                          {index + 1}
                        </span>
                      </div>
                      <div className="col-span-4">
                        <span className="text-white font-medium">{holder.name}</span>
                        {isHighChance && (
                          <motion.span 
                            className="ml-2 text-xs bg-gradient-to-r from-[#ff0080]/20 to-[#c026d3]/20 text-[#ff4da6] px-2 py-0.5 rounded-full border border-[#ff0080]/30"
                            animate={{ opacity: [0.7, 1, 0.7] }}
                            transition={{ duration: 2, repeat: Infinity }}
                          >
                            Higher Chance
                          </motion.span>
                        )}
                      </div>
                      <div className="col-span-2 text-center">
                        <span className="text-white font-bold text-lg">{holder.tickets}</span>
                      </div>
                      <div className="col-span-5">
                        <div className="h-3 bg-white/10 rounded-full overflow-hidden">
                          <motion.div
                            className="h-full bg-gradient-to-r from-[#ff0080] via-[#ff4da6] to-[#c026d3] rounded-full relative"
                            initial={{ width: 0 }}
                            whileInView={{ width: `${probability}%` }}
                            viewport={{ once: true }}
                            transition={{ duration: 1, delay: index * 0.03, ease: "easeOut" }}
                          >
                            <motion.div
                              className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/30 to-white/0"
                              animate={{ x: [-100, 200] }}
                              transition={{ duration: 2, repeat: Infinity, repeatDelay: 1 }}
                            />
                          </motion.div>
                        </div>
                      </div>
                    </div>

                    {/* Mobile Layout */}
                    <div className="md:hidden p-3">
                      <div className="grid grid-cols-12 gap-2 items-center">
                        <div className="col-span-2 text-center">
                          <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold ${
                            isHighChance 
                              ? 'bg-gradient-to-br from-[#ff0080] to-[#c026d3] text-white shadow-[0_0_10px_rgba(255,0,128,0.4)]' 
                              : 'bg-white/10 text-white/70'
                          }`}>
                            {index + 1}
                          </span>
                        </div>
                        <div className="col-span-6">
                          <p className="text-white font-medium text-sm truncate">{holder.name}</p>
                          {isHighChance && (
                            <span className="text-[10px] bg-gradient-to-r from-[#ff0080]/20 to-[#c026d3]/20 text-[#ff4da6] px-1.5 py-0.5 rounded-full border border-[#ff0080]/30">
                              Higher Chance
                            </span>
                          )}
                        </div>
                        <div className="col-span-4 text-center">
                          <span className="text-white font-bold">{holder.tickets}</span>
                        </div>
                      </div>
                      {/* Progress bar for mobile */}
                      <div className="mt-2 h-2 bg-white/10 rounded-full overflow-hidden">
                        <motion.div
                          className="h-full bg-gradient-to-r from-[#ff0080] via-[#ff4da6] to-[#c026d3] rounded-full"
                          initial={{ width: 0 }}
                          whileInView={{ width: `${probability}%` }}
                          viewport={{ once: true }}
                          transition={{ duration: 1, delay: index * 0.03, ease: "easeOut" }}
                        />
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </motion.div>
          )}

          {/* Disclaimer */}
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="mt-6 text-center text-white/40 text-sm bg-white/5 border border-white/10 rounded-xl p-4"
          >
            This is a ticket-based draw challenge. Higher tickets increase chances but do not guarantee winning rank or prize.
          </motion.p>
        </div>
      </section>

      {/* Motivation Section */}
      <section className="py-16 px-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0d0015] via-[#1a0525] to-[#0d0015]" />
        <motion.div 
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-[#ff0080]/10 rounded-full blur-[200px]"
          animate={{ scale: [1, 1.2, 1], opacity: [0.1, 0.15, 0.1] }}
          transition={{ duration: 5, repeat: Infinity }}
        />
        
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="inline-flex items-center gap-2 mb-8">
              <Zap className="w-6 h-6 text-[#ff0080]" />
              <span className="text-[#ff0080] font-mono uppercase tracking-widest text-sm">Stay Motivated</span>
            </div>

            <div className="space-y-4 md:space-y-6 mb-10">
              {motivationalQuotes.map((quote, index) => (
                <motion.p
                  key={index}
                  className="text-lg md:text-2xl lg:text-3xl text-white/80 font-light italic whitespace-nowrap"
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.15, duration: 0.6 }}
                >
                  "{quote}"
                </motion.p>
              ))}
            </div>

            <motion.a
              href={ctaLink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-gradient-to-r from-[#ff0080] to-[#c026d3] text-white font-bold text-lg px-10 py-5 rounded-full transition-all duration-300 shadow-[0_0_40px_rgba(255,0,128,0.4)] hover:shadow-[0_0_60px_rgba(255,0,128,0.6)] relative overflow-hidden group"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
            >
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0"
                animate={{ x: [-200, 200] }}
                transition={{ duration: 2, repeat: Infinity }}
              />
              <span className="relative z-10">Trade More. Increase Your Chances.</span>
              <ExternalLink className="w-5 h-5 relative z-10" />
            </motion.a>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 bg-[#050508] border-t border-white/5">
        <div className="max-w-6xl mx-auto">
          <div className="text-center">
            <img 
              src={LOGO_URL} 
              alt="Fira Indicator" 
              className="h-10 mx-auto mb-6 filter brightness-0 invert opacity-70 hover:opacity-100 transition-opacity"
            />
            <p className="text-white/40 text-sm max-w-2xl mx-auto mb-6">
              Trading involves substantial risk and is not suitable for every investor. Past performance is not indicative of future results. 
              Only trade with capital you can afford to lose. This challenge is for educational and promotional purposes only.
            </p>
            <div className="text-white/30 text-xs">
              © 2025 Fira Indicator. All rights reserved.
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
